import React from "react";
import {
  Box,
  VStack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  Flex,
  Center,
  Checkbox,
  Tooltip,
  HStack,
  IconButton,
} from "@chakra-ui/react";
import { ExternalLinkIcon, SearchIcon } from "@chakra-ui/icons";
import { Header } from "build-ts/metadata";

interface MultiListComponentProps {
  filteredHeaders: Header[];
  selectedPlanHeaders: Header[];
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onPlanToggle: (header: Header) => void;
  isSelected: (header: Header) => boolean;
}

const MultiListComponent: React.FC<MultiListComponentProps> = ({
  filteredHeaders,
  selectedPlanHeaders,
  searchTerm,
  onSearchChange,
  onPlanToggle,
  isSelected,
}) => {

  return (
    <Box
      height="100%"
      bg="gray.50"
      borderRight="1px"
      borderColor="gray.200"
      borderRadius="lg"
    >
      <VStack spacing={2} p={2} align="stretch" height="100%" overflowY="auto">
        {/* Search */}
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search plans..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            bg="white"
            size="sm"
          />
        </InputGroup>

        {/* Plan List */}
        <Box flex="1" overflowY="auto">
          <List spacing={2}>
            {filteredHeaders?.map((header) => (
              <ListItem key={header.id}>
                <Box
                  p={2}
                  bg={isSelected(header) ? "purple.50" : "white"}
                  borderRadius="md"
                  border="1px"
                  borderColor={isSelected(header) ? "purple.200" : "gray.200"}
                  cursor="pointer"
                  _hover={{ bg: isSelected(header) ? "purple.100" : "gray.50" }}
                  transition="all 0.2s"
                  onClick={() => onPlanToggle(header)}
                >
                  <Flex align="start" gap={3}>
                    <Checkbox
                      isChecked={isSelected(header)}
                      onChange={() => onPlanToggle(header)}
                      colorScheme="purple"
                      size="sm"
                      mt={3}
                    />
                    <Box flex="1">
                      <VStack align="start" spacing={1}>
                        <HStack spacing={2} align="center">
                          <Text fontWeight="medium" fontSize="sm" noOfLines={2} mb={0}>
                            {header.name || "Unnamed Plan"}
                          </Text>
                          <IconButton
                            aria-label="Open Plan"
                            icon={<ExternalLinkIcon />}
                            size="xs"
                            color="purple"
                            variant="ghost"
                            colorScheme="gray"
                            onClick={()=>{
                              window.open(`/plan/${header.id}`, "_blank");
                            }}
                          />
                        </HStack>
                      </VStack>
                      {/* Active indicator */}
                      {header?.has_active_schedule && (
                        <Tooltip label="Active" placement="top">
                          <Box
                            w="12px"
                            h="12px"
                            borderRadius="full"
                            bg="red.500"
                            display="inline-block"
                            border="1.5px solid"
                            borderColor="bg.surface"
                            mr={2}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </Flex>
                </Box>
              </ListItem>
            ))}
            {filteredHeaders?.length === 0 && (
              <Center py={8}>
                <Text color="gray.500">
                  {searchTerm
                    ? "No plans found matching your search"
                    : "No plans available"}
                </Text>
              </Center>
            )}
          </List>
        </Box>
      </VStack>
    </Box>
  );
};

export default MultiListComponent; 